import { useMutation, useQuery } from "@apollo/client";
import { FaBell, FaDiscord } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  GET_MY_NOTIFICATION_SETTINGS,
  SET_MY_NOTIFICATION_SETTINGS,
} from "../../../queries/mutation/rein-tips/notificationQueries";
import LoadingPage from "../../LoadingPage";

type Settings = {
  purchaseUpdates: boolean;
  lowBalanceWarnings: boolean;
  discordLinked: boolean;
};

const SWITCHES: {
  key: "purchaseUpdates" | "lowBalanceWarnings";
  title: string;
  description: string;
}[] = [
  {
    key: "purchaseUpdates",
    title: "Purchase & VIP updates",
    description:
      "Confirmations when you buy or renew VIP, a heads-up a few days before a subscription renews, a reminder before your VIP ends, and a note when a month of VIP you bought or your fan club membership runs out.",
  },
  {
    key: "lowBalanceWarnings",
    title: "Low-balance warnings",
    description:
      "A warning when you don't have enough crimcoins for an upcoming subscription renewal - so you can top up before it's cancelled.",
  },
];

const ALWAYS_SENT = [
  "Private redeems you buy - the DM is how they're delivered",
  "Refunds of your crimcoins",
  "A subscription being cancelled",
  "Account messages, like linking codes",
];

const TipperSettings = () => {
  const { data, loading, error, refetch } = useQuery(
    GET_MY_NOTIFICATION_SETTINGS,
    { fetchPolicy: "network-only" },
  );
  const [save, { loading: saving }] = useMutation(SET_MY_NOTIFICATION_SETTINGS, {
    // The mutation returns the fresh settings - write them straight into the
    // query so the switches update without a second round trip.
    update: (cache, { data: result }) => {
      cache.writeQuery({
        query: GET_MY_NOTIFICATION_SETTINGS,
        data: { getMyNotificationSettings: result?.setMyNotificationSettings },
      });
    },
  });

  if (loading && !data) return <LoadingPage />;
  if (error) {
    return (
      <div className="alert alert-error m-4 flex flex-wrap gap-2">
        <span>Couldn't load your settings: {error.message}</span>
        <button className="btn btn-sm" onClick={() => refetch()}>
          Retry
        </button>
      </div>
    );
  }

  const settings: Settings = data?.getMyNotificationSettings;

  const toggle = async (key: (typeof SWITCHES)[number]["key"], value: boolean) => {
    try {
      await save({ variables: { [key]: value } });
      toast.success(value ? "Turned on" : "Turned off");
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="bg-base-200 h-full overflow-y-auto p-2 sm:p-4">
      <div className="card bg-base-100 max-w-2xl mx-auto">
        <div className="card-body !p-4 gap-4">
          <h2 className="card-title">Settings</h2>

          <section>
            <h3 className="font-semibold flex items-center gap-2">
              <FaBell className="text-primary" /> Discord notifications
            </h3>
            <p className="text-sm text-base-content/60 mt-1">
              Choose which optional DMs the Reinstorm bot sends you.
            </p>

            {!settings.discordLinked && (
              <div className="alert alert-info mt-3 flex flex-row items-center gap-2 text-sm text-left">
                <FaDiscord />
                <span className="flex-1 min-w-0">
                  Link your Discord account to get DMs at all.
                </span>
                <Link to="/link-accounts" className="btn btn-sm btn-primary">
                  Link accounts
                </Link>
              </div>
            )}

            <div className="mt-3 divide-y divide-base-300 rounded-box border border-base-300">
              {SWITCHES.map((s) => (
                <label
                  key={s.key}
                  className="flex items-start justify-between gap-4 p-3 cursor-pointer"
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{s.title}</div>
                    <p className="text-xs text-base-content/60 mt-1">
                      {s.description}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    className="toggle toggle-primary shrink-0 mt-1"
                    checked={settings[s.key]}
                    onChange={(e) => toggle(s.key, e.target.checked)}
                    disabled={saving}
                    aria-label={s.title}
                  />
                </label>
              ))}
            </div>

            <div className="mt-4 text-xs text-base-content/60">
              <div className="font-semibold text-base-content/70">
                Always sent, whatever you pick:
              </div>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                {ALWAYS_SENT.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TipperSettings;
